import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { wrapThunkAction } from 'redux/utils';
import { useAppDispatch } from 'redux/store';
import { useMediaQuery } from 'react-responsive';

import { fetchAIGeneratedPrompts } from 'redux/actions/posts';
import { addAlertMessage } from 'redux/actions/alert-messages';

import { TopicPayload } from 'redux/schemas/models/activity';
import {
  ComponentTrueType,
  ComponentType,
  LectureComponent,
} from 'redux/schemas/models/lecture-component';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { AIDiscussionPrompts, errorCodesWithAlert } from 'redux/schemas/models/post';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import { getAIDiscussionPrompts } from 'redux/selectors/posts';
import { getNovoAIProperties } from 'redux/selectors/lecture-page';

import getDefaultNewComponentData from 'lecture_pages/components/data/new-component-defaults';
import { WorkflowParams } from 'lecture_pages/components/workflows';
import ConditionalWorkflow from 'lecture_pages/components/workflows/conditional-workflow';
import { Button } from 'react-bootstrap';
import { ListOfSelectableOptions } from 'shared/components/selectable-options';

import {
  ReactComponent as DiscussionLgIcon,
} from 'styles/icons/ai-lg-discussion.svg';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import { screenMdMin } from 'styles/global_defaults/media-queries';
import useCreateLectureComponent from 'lecture_pages/hooks/use-lecture-component';
import getComponentMetadata from 'lecture_pages/components/data';
import { commonStyles, containerStyles } from './styles';
import { config } from '../../../../../../../config/pendo.config.json';
import AiGeneratingLoader from '../shared/ai-generating-loader';
import AiActivityPreviewHeader from '../shared/ai-activity-preview-header';

type SelectedOptions = number[];

type AIGeneratedPromptsProps = {
  handleCloseModal: () => void;
  handleHideModal: () => void;
};

type DiscussionDefaults = {
  type: ComponentType;
  trueType: ComponentTrueType;
  post: TopicPayload['post'];
};

/**
 * Helper function to get the concatenated text of selected prompts
 * @param selectedOptions - List of selected prompts ids
 * @param prompts - List of all available prompts
 * @returns Concatenated text of the selected prompts
 */
const getSelectedPromptsText = (
  selectedOptions: SelectedOptions,
  prompts: AIDiscussionPrompts['prompts'],
): string => prompts
  .filter(prompt => selectedOptions.includes(prompt.id))
  .map(prompt => prompt.text)
  .join('<br />');

/**
 * List of AI-generated discussion prompts and the button to fire the conditional workflow of the Discussion lecture component
 * @param handleCloseModal - Callback to handle the closing event of the modal
 * @returns Modal with AI-generated discussion prompts
 */
const AIGeneratedPrompts = (props: AIGeneratedPromptsProps) => {
  const { handleCloseModal, handleHideModal } = props;
  const dispatch = useAppDispatch();
  const { fireCreateNewComponent, fireUpdateComponent } = useCreateLectureComponent();
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(
    [],
  );
  const { loading, prompts }: AIDiscussionPrompts = useSelector(
    getAIDiscussionPrompts,
  );
  const novoAIProperties = useSelector(getNovoAIProperties);

  // Adding responsive styles
  const isDesktop = useMediaQuery({
    query: `(min-width: ${screenMdMin}px)`,
  });
  const marginX = !isDesktop ? doubleSpacing : 70;
  const responsiveContainerStyles = css`
    ${containerStyles};
    margin-left: ${marginX}px;
    margin-right: ${marginX}px;
  `;

  // Identifying if the discussion is being regenerated
  let existingLectureComponent: LectureComponent<ComponentType.TOPIC> = novoAIProperties?.lectureComponentToReplace;
  const isRegenerating = !!existingLectureComponent;

  // Getting the base information to create the Discussion lecture component
  const componentType = ComponentType.TOPIC;
  const metadata = getComponentMetadata(componentType);
  const lectureComponentDefaults = getDefaultNewComponentData(
    componentType,
  ) as DiscussionDefaults;
  const baseLectureComponent = {
    ...lectureComponentDefaults,
    aiOrigin: NovoAIItemType.DISCUSSION,
    post: {
      ...lectureComponentDefaults.post,
      title: t.LECTURE_PAGES.COMPONENTS.DISCUSSION.LETS_DISCUSS(),
      body: getSelectedPromptsText(selectedOptions, prompts),
    },
  };

  // Setting the generated prompt to the existing discussion
  if (existingLectureComponent) {
    existingLectureComponent = {
      ...existingLectureComponent,
      topic: {
        ...existingLectureComponent.topic,
        body: getSelectedPromptsText(selectedOptions, prompts),
      },
    };
  }

  // Specifying the workflow to handle the creation of the Discussion lecture component
  const workflowProps: Omit<WorkflowParams, 'children'> = {
    componentType,
    metadata,
    mode: !isRegenerating ? 'new' : 'edit',
    saveNewComponent: !isRegenerating ? fireCreateNewComponent : undefined,
    save: !isRegenerating ? undefined : fireUpdateComponent,
    lectureComponent: !isRegenerating ? baseLectureComponent as LectureComponent : existingLectureComponent,
    useExistingData: true,
  };

  // Getting the AI-generated prompts from backend
  useEffect(() => {
    wrapThunkAction(
      dispatch(fetchAIGeneratedPrompts()),
    ).then((action) => {
      const { errorCode } = action.payload as AIDiscussionPrompts;

      if (errorCode) {
        handleCloseModal();

        let errorMessage = '';
        let errorHeader = '';
        if (errorCodesWithAlert.has(errorCode)) {
          errorHeader = t.FORM.FAILED();
          errorMessage = t.LECTURE_PAGES.COMPONENTS.DISCUSSION.GENERATED_MESSAGES.TOO_SHORT();
        } else {
          errorHeader = t.FORM.OOPS();
          errorMessage = t.FORM.ERROR_SOMETHING_WRONG_SHORT();
        }

        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: errorHeader,
          message: errorMessage,
        }));
      }
    });
  }, []);

  if (loading) {
    return (
      <section className='flex-column align-items-center' css={commonStyles}>
        <AiGeneratingLoader
          customDescription={t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.SINGLE_SUGGEST_DESCRIPTION()}
        />
      </section>
    );
  }

  return (
    <div css={responsiveContainerStyles} className='mb-2'>
      <div className='container mt-3 px-0'>
        <section className='results'>
          {!loading && prompts?.length > 0 && (
            <>
              <AiActivityPreviewHeader
                iconComponent={DiscussionLgIcon}
                header={t.LECTURE_PAGES.COMPONENTS.COURSE_DISCUSSION.GEN_AI.DISCUSSION_PROMPTS()}
              />
              <p className='text-regular bold gray-1 mb-0'>
                {t.LECTURE_PAGES.COMPONENTS.DISCUSSION.GENERATED_PROMPTS()}
              </p>
              <ListOfSelectableOptions
                options={prompts}
                dataQa={
                  config.pendo.lectureEdit.aiGeneratedDiscussionPrompts.generatedPrompt
                }
                onSelectionChange={newSelectedOptions => setSelectedOptions(newSelectedOptions)}
              />
            </>
          )}
        </section>

        <section className='button-bar mb-6'>
          <Button
            variant='secondary'
            onClick={handleCloseModal}
            data-qa={config.pendo.lectureEdit.aiGeneratedDiscussionPrompts.cancel}
          >
            {t.FORM.CANCEL()}
          </Button>

          <ConditionalWorkflow {...workflowProps}>
            <Button
              variant='primary'
              disabled={selectedOptions.length === 0}
              onClick={handleHideModal}
              data-qa={
                config.pendo.lectureEdit.aiGeneratedDiscussionPrompts.createDiscussion
              }
            >
              {!isRegenerating
                ? t.LECTURE_PAGES.COMPONENTS.DISCUSSION.CREATE_DISCUSSION()
                : t.LECTURE_PAGES.COMPONENTS.DISCUSSION.UPDATE_DISCUSSION()}
            </Button>
          </ConditionalWorkflow>
        </section>
      </div>
    </div>
  );
};

export default AIGeneratedPrompts;

